import { Component, inject, OnInit } from '@angular/core';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import Uppy from '@uppy/core';

import { GeoPoint } from '@angular/fire/firestore';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { Group } from '../../../models/group.model';
import { AuthService } from '../../../services/auth.service';
import * as groupActions from '../../../state/actions/group.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { ContactComponent } from '../../components/dropdowns/contact/contact.component';
import { CountryComponent } from '../../components/dropdowns/country/country.component';
import { AddLocationComponent } from '../../components/maps/add-location/add-location.component';

@Component({
  selector: 'app-edit-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    ContactComponent,
    CountryComponent,
    BtnSubmitComponent,
    BtnBackComponent,
    AddLocationComponent,
  ],
  templateUrl: './edit-group.component.html',
  styleUrls: ['./edit-group.component.css'],
})
export class EditGroupComponent implements OnInit {
  private store: Store<AppState> = inject(Store<AppState>);
  private auth: AuthService = inject(AuthService);
  private toastr: ToastrService = inject(ToastrService);
  private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly storage: Storage = inject(Storage);

  pageForm: FormGroup;

  message: string = '';

  groupId: string | null = null;
  group: Group | null | undefined;

  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    showSelectedFiles: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };

  working: boolean = false;

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this._activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
      });

    this.pageForm = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(50),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(1000),
      ]),
      primaryContact: new FormControl({}),
      secondaryContact: new FormControl({}),
      email: new FormControl('', [Validators.email]),
      buildingNo: new FormControl(''),
      buildingName: new FormControl(''),
      addressLine1: new FormControl(''),
      addressLine2: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      locationName: new FormControl(''),
      position: new FormControl(''),
      fiscalYearStart: new FormControl(''),
      fiscalYearEnd: new FormControl(''),
      isAiEnabled: new FormControl(true),
      isChatAllowed: new FormControl(true),
      currency: new FormControl('usd', [Validators.required]),
      bibleType: new FormControl('protestant', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.pageForm.patchValue({
          name: group?.name,
          description: group?.description,
          email: group?.email,
          primaryContact: group?.primaryContact ?? {},
          secondaryContact: group?.secondaryContact ?? {},
          buildingNo: group?.address?.buildingNo,
          buildingName: group?.address?.buildingName,
          addressLine1: group?.address?.addressLine1,
          addressLine2: group?.address?.addressLine2,
          city: group?.address?.city,
          country: group?.address?.country,
          locationName: group?.location?.name,
          position: group?.location?.position,
          fiscalYearStart: this.toDateString(group?.fiscalYearStart),
          fiscalYearEnd: this.toDateString(group?.fiscalYearEnd),
          isAiEnabled: group?.isAiEnabled !== false,
          isChatAllowed: group?.isChatAllowed !== false,
          currency: group?.currency?.toLowerCase() || 'usd',
          bibleType: group?.bibleType || 'protestant',
        });
        this.group = group;
      });

    this.store
      .select(groupSelector.inProgress)
      .pipe(takeUntil(this.exit$))
      .subscribe((inProgress) => {
        this.working = inProgress;
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  private toDateString(dateVal: any): string {
    if (!dateVal) return '';
    try {
      const d = dateVal.seconds ? new Date(dateVal.seconds * 1000) : new Date(dateVal);
      return d.toISOString().substring(0, 10);
    } catch (e) {
      return '';
    }
  }

  setPosition(position: GeoPoint | null) {
    this.pageForm.patchValue({ position: position });
  }

  update() {
    if (this.pageForm.invalid || !this.groupId) return;

    const file: any = this.uppy.getFiles()[0];

    const uid = this.auth.getUid();

    if (file != null && uid != null) {
      this.working = true;

      const fileExtn = file.name.split('.').pop();

      const storageRef = ref(
        this.storage,
        `groups/${this.groupId}/${this.groupId}.${fileExtn}`
      );

      const metadata = {
        contentType: file.type,
        customMetadata: {
          uid: uid!,
          groupId: this.groupId,
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file.data, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.message = `${progress.toFixed(2)}%`;
        },
        (error) => {
          this.toastr.error(
            'Group image could not be updated. Please try again later.',
            'Image upload Failed'
          );
          this.working = false;
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.updateDatabaseEntry(downloadURL);
          });
        }
      );
    } else {
      // Update group without Image
      console.log('Update group without Image');
      this.updateDatabaseEntry();
    }
  }

  updateDatabaseEntry(downloadURL?: string) {
    let data: any = {
      name: this.pageForm.value.name,
      description: this.pageForm.value.description,
      email: this.pageForm.value.email,
      fiscalYearStart: this.pageForm.value.fiscalYearStart ? new Date(this.pageForm.value.fiscalYearStart) : null,
      fiscalYearEnd: this.pageForm.value.fiscalYearEnd ? new Date(this.pageForm.value.fiscalYearEnd) : null,
      isAiEnabled: this.pageForm.value.isAiEnabled !== false,
      isChatAllowed: this.pageForm.value.isChatAllowed !== false,
      currency: this.pageForm.value.currency || 'usd',
      bibleType: this.pageForm.value.bibleType || 'protestant',
      address: {
        buildingNo: this.pageForm.value.buildingNo ?? '',
        buildingName: this.pageForm.value.buildingName ?? '',
        addressLine1: this.pageForm.value.addressLine1 ?? '',
        addressLine2: this.pageForm.value.addressLine2 ?? '',
        city: this.pageForm.value.city,
        country: this.pageForm.value.country,
      },
    };

    if (this.pageForm.value.primaryContact.contact) {
      data.primaryContact = this.pageForm.value.primaryContact;
    }

    if (this.pageForm.value.secondaryContact.contact) {
      data.secondaryContact = this.pageForm.value.secondaryContact;
    }

    if (downloadURL != undefined) {
      data.imageUrl = downloadURL;
    }

    if (this.pageForm.get('position')?.value) {
      data.location = {
        id: this.groupId,
        position: this.pageForm.get('position')?.value,
        name: data.name,
      };
    }

    data.createdBy = this.group?.createdBy;

    // console.log(`Update group form: ${JSON.stringify(data, null, 2)}`);

    this.store.dispatch(
      groupActions.update({ id: this.groupId!, group: data })
    );
  }
}
